import dayjs, { Dayjs } from 'dayjs';
import React from 'react';
import TimeAutocomplete from './TimeAutocomplete';
import { Grid, TextFieldPropsSizeOverrides } from "@mui/material";
import { OverridableStringUnion } from "@mui/types";
import { getEndTimeOptionsAfterStart, isSameDayTimeRangeInverted, minutesOfDayFromDateTime } from '@/utils/dateTimeHelpers';

export interface TimeScheduleState {
  startTime: Dayjs | null;
  endTime: Dayjs | null;
}

interface TimeScheduleSectionProps {
  startTime: Dayjs | null;
  endTime: Dayjs | null;
  onChange: (field: 'startTime' | 'endTime', value: Dayjs | null) => void;
  size?: OverridableStringUnion<"small" | "medium", TextFieldPropsSizeOverrides>;
}

const TimeScheduleSection: React.FC<TimeScheduleSectionProps> = ({
  startTime,
  endTime,
  onChange,
  size,
}) => {
  const endTimeOptions = getEndTimeOptionsAfterStart(startTime);

  const isRangeInverted = isSameDayTimeRangeInverted(startTime, endTime);

  const handleTimeUpdate = (
    field: 'startTime' | 'endTime',
    timeString: string
  ) => {
    // 1. Handle empty input (clear the value)
    if (!timeString) {
      onChange(field, null);
      return;
    }

    // 2. Parse the HH:mm string
    const [hoursStr, minutesStr] = timeString.split(':');
    const hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);

    if (isNaN(hours) || isNaN(minutes)) {
      return;
    }

    // 3. Calculate new Date object based on current prop value or current time
    const currentVal = field === 'startTime' ? startTime : endTime;
    const baseDate = currentVal || dayjs();
    const newDate = baseDate.hour(hours).minute(minutes);
    const newMinutes = hours * 60 + minutes;

    // 4. Pass updated object back to parent
    if (field === 'startTime') {
      if (endTime && newMinutes >= minutesOfDayFromDateTime(endTime)) {
        onChange('startTime', newDate);
        onChange('endTime', null);
        return;
      }
      onChange('startTime', newDate);
      return;
    }

    if (startTime && newMinutes <= minutesOfDayFromDateTime(startTime)) {
      return;
    }

    onChange('endTime', newDate);
  };

  return (
    <Grid container size={12} spacing={2}>
      <Grid size={6}>
        <TimeAutocomplete
          label="Check In Time"
          // Convert Dayjs -> String for the input
          time={startTime ? startTime.format('HH:mm') : ''}
          // Convert String -> Dayjs logic via handler
          onTimeChange={(newVal) => handleTimeUpdate('startTime', newVal)}
          error={!startTime}
          helperText={!startTime ? "Start Time is required" : ""}
          {...(size && { size })}
        />
      </Grid>
      <Grid size={6}>
        <TimeAutocomplete
          label="Check Out Time"
          // Convert Dayjs -> String for the input
          time={endTime ? endTime.format('HH:mm') : ''}
          // Convert String -> Dayjs logic via handler
          onTimeChange={(newVal) => handleTimeUpdate('endTime', newVal)}
          options={endTimeOptions}
          error={!endTime || isRangeInverted}
          helperText={!endTime ? "End Time must be valid" : ""}
          {...(size && { size })}
        />
      </Grid>

    </Grid>
  );
}

export default TimeScheduleSection;
